import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ChartBarIcon,
    BarsArrowDownIcon,
    ArrowUpIcon,
    ArrowDownIcon,
    DocumentArrowDownIcon,
    TrophyIcon,
    BoltIcon,
    Squares2X2Icon,
    EyeIcon // FIX: Added EyeIcon to the import list
} from '@heroicons/react/24/outline';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    RadialLinearScale,
    PointElement,
    LineElement,
    Filler
} from 'chart.js';
import { Bar, Radar } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    RadialLinearScale,
    PointElement,
    LineElement,
    Filler
);

// Mock data for a complete performance report
const mockPerformanceData = [
    { id: 1, name: 'Jane Doe', department: 'Technology', score: 4.8, goalsCompleted: 5, lastReview: '2024-06-25' },
    { id: 2, name: 'John Smith', department: 'Sales', score: 4.2, goalsCompleted: 4, lastReview: '2024-05-10' },
    { id: 3, name: 'Emily White', department: 'HR', score: 4.5, goalsCompleted: 3, lastReview: '2024-06-01' },
    { id: 4, name: 'David Lee', department: 'Marketing', score: 4.9, goalsCompleted: 5, lastReview: '2024-07-02' },
    { id: 5, name: 'Sarah Chen', department: 'Technology', score: 3.8, goalsCompleted: 2, lastReview: '2024-06-15' },
];

const mockSummary = {
    avgScore: 4.44,
    goalsCompletedRate: 85,
    topPerformer: 'David Lee'
};

const mockQuarterlyData = {
    labels: ['Q1 2024', 'Q2 2024', 'Q3 2024'],
    datasets: [
        {
            label: 'Avg. Performance Score',
            data: [4.2, 4.4, 4.6],
            backgroundColor: 'rgba(75, 192, 192, 0.6)',
            borderColor: 'rgba(75, 192, 192, 1)',
            borderWidth: 1,
        },
    ],
};

const mockSkillData = {
    labels: ['Problem Solving', 'Teamwork', 'Communication', 'Leadership', 'Technical Skills'],
    datasets: [
        {
            label: 'Avg. Skill Rating',
            data: [4.5, 4.8, 4.2, 4.0, 4.7],
            backgroundColor: 'rgba(153, 102, 255, 0.2)',
            borderColor: 'rgba(153, 102, 255, 1)',
            borderWidth: 2,
        },
    ],
};

const PerformanceReports = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'score', direction: 'descending' });

    const filteredEmployees = useMemo(() => {
        return mockPerformanceData.filter(employee =>
            employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            employee.department.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [searchTerm]);

    const sortedEmployees = useMemo(() => {
        let sortableEmployees = [...filteredEmployees];
        if (sortConfig.key) {
            sortableEmployees.sort((a, b) => {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];
                if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return sortableEmployees;
    }, [filteredEmployees, sortConfig]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return <BarsArrowDownIcon className="w-4 h-4 ml-1 text-gray-400" />;
        return sortConfig.direction === 'ascending' ? <ArrowUpIcon className="w-4 h-4 ml-1" /> : <ArrowDownIcon className="w-4 h-4 ml-1" />;
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-pink-100 to-purple-100 p-8">
            <motion.div
                initial={{ opacity: 0, y: -30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="max-w-7xl mx-auto"
            >
                {/* Header & Controls Section */}
                <div className="flex flex-col md:flex-row items-center justify-between mb-8 pb-4 border-b-2 border-purple-300">
                    <h1 className="text-4xl font-extrabold text-purple-800 tracking-tight mb-4 md:mb-0">
                        Performance Reports
                    </h1>
                    <div className="flex items-center space-x-4">
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="flex items-center space-x-2 px-6 py-2 bg-purple-600 text-white rounded-full shadow-lg hover:bg-purple-700 transition-colors"
                        >
                            <DocumentArrowDownIcon className="h-5 w-5" />
                            <span className="font-semibold">Export Report</span>
                        </motion.button>
                    </div>
                </div>
                ---
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <motion.div
                        className="bg-white rounded-3xl shadow-xl p-6"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.2, duration: 0.5 }}
                    >
                        <h3 className="text-lg font-bold text-gray-800">Average Score</h3>
                        <p className="text-4xl font-extrabold text-indigo-600 mt-2">{mockSummary.avgScore}/5.0</p>
                    </motion.div>
                    <motion.div
                        className="bg-white rounded-3xl shadow-xl p-6"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.3, duration: 0.5 }}
                    >
                        <h3 className="text-lg font-bold text-gray-800">Goals Completed Rate</h3>
                        <p className="text-4xl font-extrabold text-teal-600 mt-2">{mockSummary.goalsCompletedRate}%</p>
                    </motion.div>
                    <motion.div
                        className="bg-white rounded-3xl shadow-xl p-6"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.4, duration: 0.5 }}
                    >
                        <h3 className="text-lg font-bold text-gray-800">Top Performer</h3>
                        <p className="text-3xl font-extrabold text-green-600 mt-2">{mockSummary.topPerformer}</p>
                        <p className="text-sm text-gray-500">Based on latest review</p>
                    </motion.div>
                </div>
                ---
                {/* Charts & Search */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                    <motion.div
                        className="lg:col-span-2 bg-white rounded-3xl shadow-xl p-6"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5, duration: 0.6 }}
                    >
                        <div className="flex items-center space-x-2 mb-4 text-purple-600">
                            <ChartBarIcon className="h-6 w-6" />
                            <h3 className="text-lg font-bold">Quarterly Performance Trends</h3>
                        </div>
                        <div className="h-72">
                            <Bar
                                data={mockQuarterlyData}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: { legend: { position: 'top' } },
                                    scales: { y: { beginAtZero: true, max: 5.0 } }
                                }}
                            />
                        </div>
                    </motion.div>
                    <motion.div
                        className="bg-white rounded-3xl shadow-xl p-6"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6, duration: 0.6 }}
                    >
                        <div className="flex items-center space-x-2 mb-4 text-purple-600">
                            <Squares2X2Icon className="h-6 w-6" />
                            <h3 className="text-lg font-bold">Average Skill Ratings</h3>
                        </div>
                        <div className="h-72">
                            <Radar
                                data={mockSkillData}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    scales: { r: { beginAtZero: true, max: 5.0 } }
                                }}
                            />
                        </div>
                    </motion.div>
                </div>
                ---
                {/* Employee Performance Table */}
                <motion.div
                    className="mt-8 bg-white rounded-3xl shadow-xl overflow-hidden"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7, duration: 0.6 }}
                >
                    <div className="px-6 pt-6 flex items-center justify-between">
                        <h3 className="text-lg font-bold text-gray-800">Detailed Performance Breakdown</h3>
                        <input
                            type="text"
                            placeholder="Search employees..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-4 pr-4 py-2 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all duration-300 shadow-sm"
                        />
                    </div>
                    <div className="overflow-x-auto mt-4">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    {['name', 'department', 'score', 'goalsCompleted', 'lastReview'].map(key => (
                                        <th
                                            key={key}
                                            onClick={() => requestSort(key)}
                                            className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                        >
                                            <div className="flex items-center">
                                                {key.replace(/([A-Z])/g, ' $1').charAt(0).toUpperCase() + key.replace(/([A-Z])/g, ' $1').slice(1)}
                                                {getSortIcon(key)}
                                            </div>
                                        </th>
                                    ))}
                                    <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                <AnimatePresence initial={false}>
                                    {sortedEmployees.length > 0 ? (
                                        sortedEmployees.map(employee => (
                                            <motion.tr
                                                key={employee.id}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, x: -20, transition: { duration: 0.3 } }}
                                                transition={{ duration: 0.5, ease: 'easeOut' }}
                                                className="hover:bg-gray-50 transition-colors"
                                            >
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{employee.name}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{employee.department}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    <span className="font-semibold">{employee.score}/5.0</span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{employee.goalsCompleted}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{employee.lastReview}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <motion.button
                                                        whileHover={{ scale: 1.1 }}
                                                        whileTap={{ scale: 0.9 }}
                                                        className="text-indigo-600 hover:text-indigo-900 transition-colors"
                                                    >
                                                        <EyeIcon className="h-5 w-5" />
                                                    </motion.button>
                                                </td>
                                            </motion.tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="6" className="px-6 py-4 text-center text-gray-500">No employees found.</td>
                                        </tr>
                                    )}
                                </AnimatePresence>
                            </tbody>
                        </table>
                    </div>
                </motion.div>
            </motion.div>
        </div>
    );
};

export default PerformanceReports;